import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import SlimWaveDivider from './SlimWaveDivider';

const Footer: React.FC = () => {
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const clearBg = location.pathname === '/' || location.pathname === '/home-draft';

  const quickLinks = [
    { name: 'About Us', href: '/about' },
    { name: 'Internet Plans', href: '/plans' },
    { name: 'Kingman Internet', href: '/internet-kingman-az' },
    { name: 'Golden Valley Internet', href: '/internet-golden-valley-az' },
    { name: 'Phone Service', href: '/phone-service' },
    { name: 'Kingman Phone', href: '/phone-kingman-az' },
    { name: 'Golden Valley Phone', href: '/phone-golden-valley-az' },
    { name: 'How It Works', href: '/how-it-works' },
    { name: 'Support', href: '/support' },
    { name: 'Chat with Us', href: '/chat' },
    { name: 'Contact Us', href: '/contact' },
  ];

  const services = [
    { name: 'Residential Internet', href: '/plans' },
    { name: 'Business Internet', href: '/plans' },
    { name: 'Kingman, AZ Service', href: '/internet-kingman-az' },
    { name: 'Golden Valley, AZ Service', href: '/internet-golden-valley-az' },
    { name: 'Phone Service', href: '/phone-service' },
    { name: 'Customer Support', href: '/support' },
  ];

  const legal = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
  ];

  return (
    <footer
      className={`relative z-10 text-white ${clearBg ? 'bg-transparent' : 'bg-gray-900'}`}
    >
      {!clearBg && <SlimWaveDivider className="bg-gray-900" />}
      {clearBg && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" aria-hidden="true">
          <div className="border-t border-white/25" />
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="col-span-1 lg:col-span-2">
            <div className="mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/images/AirmaxLogoWhite.png`}
                alt="Airmax Internet"
                className="h-12 w-auto"
              />
            </div>
            <p
              className={`mb-6 max-w-md ${
                clearBg
                  ? 'text-white/80 drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]'
                  : 'text-gray-300'
              }`}
            >
              Local ISP for Kingman and Golden Valley, Arizona — high-speed wireless internet with WAVE Fiber Speeds,
              phone service, and Arizona-based support.
            </p>
            <div className="space-y-3">
              <div className={`flex items-center ${clearBg ? 'text-white/80' : 'text-gray-300'}`}>
                <PhoneIcon className="h-5 w-5 mr-3 text-blue-400" />
                <a href="tel:928-565-6707" className="hover:text-blue-400 transition-colors">
                  928-565-6707
                </a>
              </div>
              <div className={`flex items-center ${clearBg ? 'text-white/80' : 'text-gray-300'}`}>
                <EnvelopeIcon className="h-5 w-5 mr-3 text-blue-400" />
                <span>airmaxhelpdesk@gmail.com</span>
              </div>
              <div className={`flex items-center ${clearBg ? 'text-white/80' : 'text-gray-300'}`}>
                <MapPinIcon className="h-5 w-5 mr-3 text-blue-400" />
                <span>Serving Golden Valley, Kingman, and surrounding areas</span>
              </div>
              <div className={`flex items-center ${clearBg ? 'text-white/80' : 'text-gray-300'}`}>
                <ClockIcon className="h-5 w-5 mr-3 text-blue-400" />
                <span>Phone: Mon–Fri 9 AM–5 PM (AZ) · Online Support Center 24/7</span>
              </div>
            </div>
          </div>

          <div>
            <h3
              className={`text-lg font-semibold mb-4 ${
                clearBg ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]' : ''
              }`}
            >
              Quick Links
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`hover:text-blue-400 transition-colors flex items-center ${
                      clearBg ? 'text-white/80' : 'text-gray-300'
                    }`}
                  >
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3
              className={`text-lg font-semibold mb-4 ${
                clearBg ? 'drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]' : ''
              }`}
            >
              Services
            </h3>
            <ul className="space-y-2">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    onClick={() => window.scrollTo(0, 0)}
                    className={`hover:text-blue-400 transition-colors flex items-center ${
                      clearBg ? 'text-white/80' : 'text-gray-300'
                    }`}
                  >
                    <ChevronRightIcon className="h-4 w-4 mr-1" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className={`mt-8 pt-8 ${clearBg ? 'border-t border-white/20' : 'border-t border-gray-800'}`}>
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className={`text-sm mb-4 md:mb-0 ${clearBg ? 'text-white/60' : 'text-gray-400'}`}>
              © {currentYear} Airmax Internet L.L.C. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <a
                href="https://www.facebook.com/profile.php?id=61552173193565"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-400 hover:text-blue-300 transition-colors"
                aria-label="Follow us on Facebook"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              {legal.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`text-sm transition-colors hover:text-blue-400 ${
                    clearBg ? 'text-white/60' : 'text-gray-400'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
